import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { AssetSubscriptionDtoAll } from "@/common/mapping/mappers/assetSubscription";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { AssetSubscriptionDto, AssetSubscriptionReferenceDto } from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getContractInlineTitleShort } from "../Contract/ContractInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { AssetSubscriptionViewModal } from "./View/AssetSubscriptionViewModal";

const defaultDisplayProps = { contract: true };
type DisplayProps = typeof defaultDisplayProps;

export const getAssetSubscriptionInlineTitle = (
  data: Nil<AssetSubscriptionDtoAll>,
  displayProps?: DisplayProps,
) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          data.plan?.name,
          enumService.getEnumValueName(ApiEnumName.SubscriptionStatus, data.status),
          displayProps?.contract && data.contract
            ? getContractInlineTitleShort(data.contract)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface AssetSubscriptionInlineProps extends BaseEntityInlineInheritableProps {
  entity: AssetSubscriptionDto | AssetSubscriptionReferenceDto | null | undefined;
  entityId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

/** Inline brief information about the entity. */
export default function AssetSubscriptionInline({
  entity,
  entityId,
  displayProps,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: AssetSubscriptionInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.assetSubscriptionsApi.apiV1AssetSubscriptionsAssetSubscriptionIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      assetSubscriptionId: entityId!,
    },
    {
      deps: [entityId],
      skip: !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={request}
        icon={<AppIcon of='entity' inText />}
        content={
          getAssetSubscriptionInlineTitle(entity, displayPropsComputed) || "Asset subscription"
        }
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Asset subscription"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <AssetSubscriptionViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                assetSubscriptionId: entityIdComputed,
                assetSubscription: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
